<template>
	<div class="InputText" :class="classes">
		<label class="InputText-label" :for="id">
			<span>{{ label }}</span>
			<div v-if="labelEmphasis">
				<strong>{{ labelEmphasis }}</strong>
			</div>
		</label>
		<input
			class="InputText-field"
			:id="id"
			:maxlength="maxlength"
			:name="name"
			:placeholder="placeholder"
			:readonly="readonly"
			:disabled="disabled"
			:required="required"
			:pattern="pattern"
			:type="type"
			:autocomplete="preventAutocomplete ? 'off' : false"
			:data-lpignore="preventAutocomplete ? 'true' : false"
			:spellcheck="!preventAutocomplete"
			v-model="inputValue"
			@input="emitInput"
			@change="emitChange"
		/>
		<div v-if="helptext">
			<p class="FormHelp">{{ helptext }}</p>
		</div>
		<div v-if="error">
			<p class="FormHelp FormHelp--error">{{ errorMessage }}</p>
		</div>
	</div>
</template>

<script>
export default {
	data() {
		return {
			inputValue: this.value,
		};
	},
	emits: ['input', 'update:value', 'change'],
	model: {
		prop: 'value',
		event: 'input',
	},
	props: {
		id: {
			type: String,
			default: 'id-' + Math.random().toString(36).substr(2, 9),
		},
		label: {
			type: String,
			required: true,
		},
		labelEmphasis: {
			type: String,
			default: '',
		},
		helptext: {
			type: String,
			default: '',
		},
		errorMessage: {
			type: String,
			default: '',
		},
		type: {
			type: String,
			default: 'text',
		},
		placeholder: {
			type: String,
			default: '',
		},
		value: {
			type: String,
			default: '',
		},
		name: {
			type: String,
			default: '',
		},
		pattern: {
			type: [String, Boolean],
			default: false,
		},
		title: {
			type: String,
			default: '',
		},
		/**
		 * Max characters allowed
		 */
		maxlength: {
			type: Number,
			required: false,
		},
		size: {
			type: String,
			default: '',
		},
		error: {
			type: Boolean,
			default: false,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		reverse: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		required: {
			type: Boolean,
			default: false,
		},
		/**
		 * When true, prevents browser and last pass extension from auto-filling or spellchecking input
		 */
		preventAutocomplete: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes: function () {
			let classes = [];
			const prefix = 'InputText--';
			if (this.size.length) {
				let size = this.size;
				if (size === 'sm') {
					size = 'small';
				}
				if (size === 'lg') {
					size = 'large';
				}
				classes.push(prefix + size);
			}
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			if (this.error) {
				classes.push(prefix + 'error');
			}
			return classes.join(' ');
		},
	},
	watch: {
		value() {
			this.inputValue = this.value;
		},
	},
	methods: {
		emitInput(e) {
			this.$emit('input', e.target.value); // for use with v-model in Vue 2
			this.$emit('update:value', e.target.value); // for use with v-model in Vue 3
		},
		emitChange(e) {
			this.$emit('change', e.target.value);
		},
	},
};
</script>

<style lang="scss">
.InputText {
	@include stack-md;
	@include text(3);
	text-align: left;
	[dir='rtl'] & {
		text-align: right;
	}
	.InputText-field {
		width: 100%;
		height: auto;
		margin-bottom: 0;
		@include text(4);
		@include inset-sm;
		box-shadow: none;
		border: 1px solid $color-form-outline;
		border-radius: $radius-sm;
	}
	label {
		@include text(3);
		@include stack-xs;
		display: block;
	}
	.InputText-errorMessage {
		display: none;
		@include text(3);
		color: $color-text-error;
	}
}

.InputText--small {
	@include stack-sm;
	@include text(2);
	.InputText-field {
		@include text(3);
		@include inset-xs;
	}
	label {
		@include text(2);
	}
	.InputText-errorMessage {
		@include text(2);
	}
}

.InputText--large {
	@include text(4);
	.InputText-field {
		@include text(5);
		@include inset-md;
	}
	label {
		@include text(3);
	}
}

// TODO: deprecate
.InputText--hasError {
	color: $color-text-error;
	.InputText-field {
		border-color: $color-text-error;
	}
}

// TODO: deprecate
.InputText-field--hasError {
	color: $color-text-error;
	border-color: $color-text-error;
}

.InputText--error {
	.InputText-field {
		border-color: $color-text-error;
		border-width: 2px;
	}
	label {
		color: $color-text-error;
		font-weight: bold;
	}
	// TODO: deprecate
	.InputText-errorMessage {
		display: block;
	}
}

.InputText--flex {
	flex: 1 0 100%;
	@include breakpoint(medium) {
		flex: 1 0 0;
	}
	margin-right: 2px;
	[dir='rtl'] & {
		margin-right: 0;
		margin-left: 2px;
	}
	&:last-child {
		margin-right: 0;
		[dir='rtl'] & {
			margin-left: 0;
		}
	}
}

.InputText--compact {
	margin-bottom: 0;
	@include breakpoint(medium) {
		margin-bottom: 0.5rem;
	}
}

.InputText--emphasis {
	.InputText-field {
		font-weight: bold;
	}
}

input[type='search'] {
	text-align: left;
	[dir='rtl'] & {
		text-align: right;
	}
}

.InputText--reverse {
	label {
		color: $color-text-reverse;
	}
	.InputText-field {
		background-color: $color-black;
		box-shadow: none;
		border-color: $color-form-outline-reverse;
		color: $color-text-reverse;
		&:focus {
			outline: 2px solid $color-form-outline-focus-reverse;
		}
	}
	&.InputText--hasError {
		.InputText-field {
			border-color: $color-text-error-reverse;
		}
		.FormHelp--error {
			color: $color-text-error-reverse;
		}
	}
}
</style>
